import React from "react";
import { Mail, Phone, MapPin, Send, MessageCircle } from "lucide-react";
import { businessInfo } from "../data/mock";

const ContactSection = ({ onOpenForm }) => {
  return (
    <section id="contact" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-custom px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">
            Contact Our Trademark Experts
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to protect your brand? Our team of experienced trademark attorneys is here to help you 
            navigate the trademark registration process. Reach out today for a free consultation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Let's Start Protecting Your Brand
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Whether you're just starting out or looking to expand your trademark portfolio, our team 
                is ready to provide expert guidance. We offer flexible consultation options to fit your 
                schedule and preferred communication method.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {/* Address */}
              <div className="group bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Our Office</h4>
                  <p className="text-gray-600 leading-relaxed">{businessInfo.address}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="group bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Phone Number</h4>
                  <a
                    href={`tel:${businessInfo.phone}`}
                    className="text-[#1e3a8a] hover:text-[#d4af37] transition-colors duration-300 text-lg"
                  >
                    {businessInfo.phone}
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Mon-Fri: 9AM - 6PM EST</p>
                </div>
              </div>

              {/* Email */}
              <div className="group bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Email Address</h4>
                  <a
                    href={`mailto:${businessInfo.email}`}
                    className="text-[#1e3a8a] hover:text-[#d4af37] transition-colors duration-300 text-lg"
                  >
                    {businessInfo.email}
                  </a>
                  <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form Preview / CTA */}
          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-2xl border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Start Your Trademark Registration
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Fill out our quick form to begin the trademark registration process. Our team will review 
              your information and contact you within 24 hours to discuss the next steps.
            </p>

            {/* Form Preview */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What would you like to trademark?
                </label>
                <input
                  type="text"
                  placeholder="e.g., Brand name, logo, slogan"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a8a] transition-all duration-300 text-sm bg-gray-50"
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Industry / Business Type
                </label>
                <input
                  type="text"
                  placeholder="e.g., Technology, Retail, Healthcare"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a8a] transition-all duration-300 text-sm bg-gray-50"
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  When do you need trademark protection?
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a8a] transition-all duration-300 bg-gray-50 text-sm"
                  disabled
                >
                  <option>As soon as possible</option>
                  <option>Within 1-3 months</option>
                  <option>Within 3-6 months</option>
                  <option>Just researching options</option>
                </select>
              </div>
            </div>

            <button
              onClick={onOpenForm}
              className="w-full bg-[#1e3a8a] text-white py-3.5 rounded-lg font-bold hover:bg-[#1e40af] transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
            >
              <span>Complete Registration Form</span>
              <Send className="w-5 h-5" />
            </button>

            <p className="text-sm text-gray-500 text-center mt-4">
              🔒 Your information is secure and confidential
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
